import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, CheckCircle2, Clock, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const statusMeta = {
  pending: { icon: Clock, cls: "bg-amber-500/10 text-amber-400", label: "Pending" },
  approved: { icon: CheckCircle2, cls: "bg-brand-green/10 text-brand-green", label: "Approved" },
  rejected: { icon: XCircle, cls: "bg-red-500/10 text-red-400", label: "Rejected" },
};

export default function BrokerVerification() {
  const { t } = useLang();
  useSEO("Broker Verification");
  const [brokers, setBrokers] = useState([]);
  const [verifs, setVerifs] = useState([]);
  const [form, setForm] = useState({ broker_id: "", account_uid: "", full_name: "", account_email: "", notes: "" });
  const [loading, setLoading] = useState(false);

  const load = () => api.get("/broker-verifications").then((r) => setVerifs(r.data)).catch(() => {});
  useEffect(() => { api.get("/brokers").then((r) => setBrokers(r.data)).catch(() => {}); load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.broker_id) { toast.error("Select a broker"); return; }
    setLoading(true);
    try {
      await api.post("/broker-verifications", form);
      toast.success("Verification submitted");
      setForm({ broker_id: "", account_uid: "", full_name: "", account_email: "", notes: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6">
      <h1 className="font-display text-2xl font-bold text-white">{t("broker_verification")}</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        <form onSubmit={submit} className="card-surface rounded-2xl p-7 space-y-4" data-testid="verification-form">
          <div className="flex items-center gap-2 text-brand-cyan mb-2"><ShieldCheck className="h-5 w-5" /><span className="font-display font-semibold">Submit verification</span></div>
          <div>
            <Label className="text-slate-300">Broker</Label>
            <Select value={form.broker_id} onValueChange={(v) => setForm({ ...form, broker_id: v })}>
              <SelectTrigger className="mt-1.5 bg-white/5 border-white/10" data-testid="verify-broker-select"><SelectValue placeholder="Select a broker" /></SelectTrigger>
              <SelectContent>{brokers.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-slate-300">Account UID / Number</Label><Input required value={form.account_uid} onChange={(e) => setForm({ ...form, account_uid: e.target.value })} data-testid="verify-uid" className="mt-1.5 bg-white/5 border-white/10" /></div>
          <div><Label className="text-slate-300">Full Name (on account)</Label><Input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="verify-name" className="mt-1.5 bg-white/5 border-white/10" /></div>
          <div><Label className="text-slate-300">Account Email</Label><Input type="email" value={form.account_email} onChange={(e) => setForm({ ...form, account_email: e.target.value })} data-testid="verify-email" className="mt-1.5 bg-white/5 border-white/10" /></div>
          <div><Label className="text-slate-300">Notes (optional)</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="verify-notes" className="mt-1.5 bg-white/5 border-white/10" /></div>
          <Button type="submit" disabled={loading} data-testid="verify-submit" className="w-full gradient-brand text-brand-navy font-semibold">{loading ? t("loading") : t("submit")}</Button>
        </form>

        <div className="space-y-3">
          <h2 className="font-display font-semibold text-white">Your submissions</h2>
          {verifs.length === 0 ? <div className="card-surface rounded-2xl p-8 text-center text-slate-500 text-sm">No verifications submitted yet.</div>
            : verifs.map((v) => {
              const m = statusMeta[v.status] || statusMeta.pending;
              return (
                <div key={v.id} className="card-surface rounded-2xl p-5" data-testid={`verif-item-${v.id}`}>
                  <div className="flex items-center justify-between">
                    <p className="font-display font-semibold text-white">{v.broker_name}</p>
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${m.cls}`}><m.icon className="h-3.5 w-3.5" /> {m.label}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">UID: {v.account_uid}</p>
                  {v.admin_note && <p className="mt-2 text-xs text-slate-400 bg-white/5 rounded-lg p-2">Admin: {v.admin_note}</p>}
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}
